import L from 'leaflet';

// Icon colors for different marker types
const ICON_COLORS = {
  event: '#3B82F6',      // Blue
  match: '#10B981',      // Green
  field: '#F59E0B',      // Amber
  player: '#8B5CF6'      // Purple
};

// Sport color mapping
const SPORT_COLORS = {
  'football': '#EF4444',      // Red
  'basketball': '#F97166',    // Red-pink
  'tennis': '#84CC16',        // Lime
  'natation': '#06B6D4',      // Cyan
  'course': '#EC4899',        // Pink
  'cyclisme': '#F59E0B',      // Amber
  'autre': '#6366F1'          // Indigo
};

export const getSportColor = (sport) => {
  const normalizedSport = sport ? String(sport).trim().toLowerCase() : null;
  return SPORT_COLORS[normalizedSport] || '#6B7280';
};

// Create custom Leaflet icons
export const createMarkerIcon = (type, sport = null) => {
  const color = type === 'event' || type === 'match' ? getSportColor(sport) : ICON_COLORS[type];
  
  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 3px solid white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${getTypeEmoji(type)}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -10],
    className: 'custom-marker'
  });
};

// Create pulsing marker icon for ongoing matches
export const createPulsingMarkerIcon = (sport) => {
  const color = getSportColor(sport);
  const emoji = getTypeEmoji('match', sport); // use sport emoji for pulsing matches
  
  return L.divIcon({
    html: `<div style="
      position: relative;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <style>
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.7;
          }
        }
        .pulse-ring {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      </style>
      <div class="pulse-ring" style="
        position: absolute;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 2px solid ${color};
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
      <div style="
        position: relative;
        background-color: ${color};
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 3px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 12px;
        z-index: 1;
      ">${emoji}</div>
    </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
    className: 'pulsing-marker'
  });
};

// Returns an emoji for a marker based on its type and, when relevant,
// the sport.  Events and matches are shown using the sport's icon rather
// than a generic calendar/ball so users can quickly distinguish a tennis
// meetup from a football game.
const getTypeEmoji = (type, sport = null) => {
  if (type === 'event' || type === 'match') {
    const sportEmojis = {
      'football': '⚽',
      'basketball': '🏀',
      'tennis': '🎾',
      'natation': '🏊',
      'course': '🏃',
      'cyclisme': '🚴',
      'autre': '📍'
    };
    
    // Normalise sport to lowercase and trim whitespace to handle variations
    const normalizedSport = sport ? String(sport).trim().toLowerCase() : null;
    // log unrecognised sports once to aid debugging
    if (normalizedSport && !(normalizedSport in sportEmojis)) {
      console.warn(`mapUtils.getTypeEmoji: unknown sport \"${sport}\" (normalized to \"${normalizedSport}\")`);
    }
    return sportEmojis[normalizedSport] || '📅'; // fallback to calendar if sport unknown
  }

  const emojis = {
    field: '🏟️',
    player: '👤'
  };

  return emojis[type] || '📍';
};

// Calculate distance between two coordinates in km
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Format marker data for display
export const formatMarkerData = (marker) => {
  if (marker.type === 'event') {
    return {
      title: marker.sport,
      description: marker.description || 'No description',
      status: marker.status,
      date: new Date(marker.date).toLocaleDateString(),
      time: marker.startTime,
      organizer: marker.organizerName,
      participants: `${marker.participantsCount}/${marker.maxParticipants || '∞'}`,
      level: marker.level
    };
  } else if (marker.type === 'match') {
    return {
      title: marker.title,
      sport: marker.sport,
      status: marker.status,
      owner: marker.ownerName,
      members: marker.membersCount,
      date: marker.date
    };
  } else if (marker.type === 'field') {
    return {
      name: marker.name,
      sport: marker.sport,
      capacity: marker.capacity,
      available: marker.available
    };
  } else if (marker.type === 'player') {
    return {
      name: marker.name,
      lastSeen: new Date(marker.lastSeen).toLocaleTimeString()
    };
  }
};

// Default map center (Paris, France)
export const DEFAULT_MAP_CENTER = {
  latitude: 48.8566,
  longitude: 2.3522,
  zoom: 13
};

// Filter markers by sport and status
export const filterMarkers = (markers, filters) => {
  return markers.filter(marker => {
    if (filters.sport && filters.sport !== 'all') {
      if (marker.type === 'event' && marker.sport !== filters.sport) return false;
      if (marker.type === 'match' && marker.sport !== filters.sport) return false;
      if (marker.type === 'field' && marker.sport !== filters.sport) return false;
    }
    
    if (filters.status && filters.status !== 'all') {
      if (marker.status && marker.status !== filters.status) return false;
    }
    
    if (filters.type && filters.type !== 'all') {
      if (marker.type !== filters.type) return false;
    }
    
    return true;
  });
};

export const MARKER_TYPES = [
  { value: 'all', label: 'Tous les marqueurs', icon: '📍' },
  { value: 'match', label: 'Matchs', icon: '⚽' },
  { value: 'event', label: 'Événements', icon: '📅' },
  { value: 'field', label: 'Terrains', icon: '🏟️' },
  { value: 'player', label: 'Joueurs en ligne', icon: '👤' }
];

export const MARKER_STATUSES = [
  { value: 'all', label: 'Tous' },
  { value: 'scheduled', label: 'Programmé' },
  { value: 'ongoing', label: 'En cours' },
  { value: 'completed', label: 'Terminé' }
];
